import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const CheckinBanner = () => {
  const { currentUser } = useAuth();
  const [activeTrips, setActiveTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    // Listen for trip plans where user is owner or helper
    const tripPlansRef = collection(db, 'tripPlans');
    const unsubscribe = onSnapshot(tripPlansRef, async (snapshot) => {
      const trips = [];
      
      for (const doc of snapshot.docs) {
        const tripData = doc.data();
        
        // Check if user is owner or helper
        const isOwner = tripData.userId === currentUser.uid;
        const isHelper = tripData.helpers?.some(helper => helper.userId === currentUser.uid);
        
        if (isOwner || isHelper) {
          // Check if trip is active and has check-in enabled
          if (tripData.status === 'active' && tripData.checkinConfig?.enabled) {
            const tripDate = new Date(tripData.tripDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Show banner if trip is today or within 3 days
            const daysDiff = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
            
            if (daysDiff >= 0 && daysDiff <= 3) {
              trips.push({
                id: doc.id,
                ...tripData,
                daysUntilTrip: daysDiff
              });
            }
          }
        }
      }
      
      setActiveTrips(trips);
      setShowBanner(trips.length > 0);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleCheckinClick = (tripId) => {
    // Navigate to check-in page
    window.location.href = `/checkin/${tripId}`;
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
  };

  if (loading) {
    return null;
  }

  if (!showBanner || activeTrips.length === 0) {
    return null;
  }

  const getBannerType = (trip) => {
    if (trip.daysUntilTrip === 0) {
      return {
        type: 'today',
        color: 'bg-red-500',
        icon: '🚨',
        title: 'วันนี้มีกิจกรรม!',
        message: `ทริป "${trip.tripName}" วันนี้`
      };
    } else if (trip.daysUntilTrip === 1) {
      return {
        type: 'tomorrow',
        color: 'bg-orange-500',
        icon: '⚠️',
        title: 'พรุ่งนี้มีกิจกรรม!',
        message: `ทริป "${trip.tripName}" พรุ่งนี้`
      };
    } else {
      return {
        type: 'upcoming',
        color: 'bg-blue-500',
        icon: '📅',
        title: 'มีกิจกรรมใกล้เข้ามา',
        message: `ทริป "${trip.tripName}" ในอีก ${trip.daysUntilTrip} วัน`
      };
    }
  };

  const primaryTrip = activeTrips[0]; // Show the most urgent trip
  const bannerInfo = getBannerType(primaryTrip);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down" style={{
      animation: 'slideDown 0.5s ease-out'
    }}>
      <div className={`${bannerInfo.color} text-white shadow-lg`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{bannerInfo.icon}</span>
              <div>
                <h3 className="font-bold text-lg">{bannerInfo.title}</h3>
                <p className="text-sm opacity-90">{bannerInfo.message}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleCheckinClick(primaryTrip.id)}
                className="bg-white text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Check-in
              </button>
              
              <button
                onClick={handleDismissBanner}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Show multiple trips if more than one */}
          {activeTrips.length > 1 && (
            <div className="mt-2 pt-2 border-t border-white/20">
              <p className="text-sm opacity-90">
                และอีก {activeTrips.length - 1} ทริปที่ใกล้เข้ามา
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckinBanner;
